import { Play, Pause, Volume2, VolumeX, Music2, SkipBack, SkipForward } from 'lucide-react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ZapButton } from '@/components/ZapButton';
import { useMusicPlayer } from '@/contexts/MusicPlayerContext';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { formatDuration } from '@/lib/musicTypes';
import { cn } from '@/lib/utils';
import { useAppContext } from '@/hooks/useAppContext';

export function PlayerBar() {
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    togglePlay,
    seek,
    setVolume,
    toggleMute,
    nextTrack,
    previousTrack,
    hasNext,
    hasPrevious,
  } = useMusicPlayer();

  const { config } = useAppContext();

  // Use artist name if available, otherwise fall back to uploader name
  const uploaderPubkey = currentTrack?.pubkey || '';
  const uploader = useAuthor(uploaderPubkey);
  const uploaderMetadata = uploader.data?.metadata;
  const uploaderName = uploaderMetadata?.name || genUserName(uploaderPubkey);

  const displayName = currentTrack?.artistName || uploaderName;

  const spinningRecordsEnabled = config.spinningRecords ?? false;

  if (!currentTrack) {
    return null;
  }

  const naddr = nip19.naddrEncode({
    kind: currentTrack.event.kind,
    pubkey: currentTrack.pubkey,
    identifier: currentTrack.id,
  });

  const backgroundStyle = currentTrack.imageUrl
    ? { backgroundImage: `url(${currentTrack.imageUrl})` }
    : currentTrack.gradient
    ? { background: `linear-gradient(135deg, ${currentTrack.gradient.join(', ')})` }
    : { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/[0.11] bg-black/10 backdrop-blur-md safari-player-fix">
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 -translate-y-full bg-black/20">
        <Slider
          variant="progress"
          value={[currentTime]}
          max={duration || 100}
          step={0.1}
          onValueChange={([value]) => seek(value)}
          className="cursor-pointer rounded-none"
        />
      </div>

      <div className="mx-auto px-4 sm:px-4 py-4 sm:py-3 max-w-4xl 2xl:max-w-5xl">
        <div className="flex items-center gap-4">
          {/* Track Info */}
          <Link
            to={`/${naddr}`}
            className="flex items-center gap-3 flex-1 min-w-0 group"
          >
            <div
              className={cn(
                "w-12 h-12 bg-cover bg-center flex-shrink-0",
                spinningRecordsEnabled ? "rounded-full record-hole" : "rounded-lg",
                spinningRecordsEnabled && isPlaying && "record-spinning"
              )}
              style={backgroundStyle}
            >
              {!currentTrack.imageUrl && (
                <div className={cn(
                  "w-full h-full flex items-center justify-center bg-black/20",
                  spinningRecordsEnabled ? "rounded-full" : "rounded-lg"
                )}>
                  <Music2 className="h-6 w-6 text-white/80" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                {currentTrack.title}
              </p>
              <p className="text-xs text-muted-foreground truncate">{displayName}</p>
            </div>
          </Link>

          {/* Controls */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Time */}
            <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
              <span>{formatDuration(currentTime)}</span>
              <span>/</span>
              <span>{formatDuration(duration)}</span>
            </div>

            {/* Zap Button */}
            <ZapButton
              event={currentTrack.event}
              size="icon"
              variant="ghost"
              className="h-11 w-11 sm:h-9 sm:w-9 bg-white/[0.06] hover:bg-white/[0.12]"
            />

            {/* Previous Button */}
            <Button
              size="icon"
              variant="ghost"
              onClick={previousTrack}
              disabled={!hasPrevious}
              className="h-11 w-11 sm:h-9 sm:w-9 rounded-full border-0 bg-white/[0.06] hover:bg-white/[0.12] disabled:opacity-30"
            >
              <SkipBack className="h-5 w-5 sm:h-4 sm:w-4" />
            </Button>

            {/* Play/Pause */}
            <Button
              size="icon"
              variant="ghost"
              onClick={togglePlay}
              className="h-11 w-11 sm:h-9 sm:w-9 rounded-full border-0 bg-white text-black hover:bg-white/90 [&_svg]:!text-black"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 sm:h-4 sm:w-4" />
              ) : (
                <Play className="h-5 w-5 sm:h-4 sm:w-4 ml-0.5" />
              )}
            </Button>

            {/* Next Button */}
            <Button
              size="icon"
              variant="ghost"
              onClick={nextTrack}
              disabled={!hasNext}
              className="h-11 w-11 sm:h-9 sm:w-9 rounded-full border-0 bg-white/[0.06] hover:bg-white/[0.12] disabled:opacity-30"
            >
              <SkipForward className="h-5 w-5 sm:h-4 sm:w-4" />
            </Button>

            {/* Volume */}
            <div className="hidden md:flex items-center gap-2 w-24">
              <Button
                size="icon"
                variant="ghost"
                onClick={toggleMute}
                className="h-8 w-8"
              >
                {isMuted ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <Volume2 className="h-4 w-4" />
                )}
              </Button>
              <Slider
                value={[isMuted ? 0 : volume]}
                max={1}
                step={0.01}
                onValueChange={([value]) => setVolume(value)}
                className="cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
